import { useState, useEffect } from 'react'
import { useSelector } from "react-redux"

import { subscribe } from '../api'
import { onUpdateCurrentBidSubscription } from '../graphql/Subscriptions'
import { Link, Redirect } from 'react-router-dom'
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { showLoading } from 'services/util'

import { getAuctionUser } from '../redux/selectors'
import { getAuctionByCode } from '../api'
import { urlQueryParser } from 'services/util'
import { getAuctionStatus } from '../util/dateString'
import CustomMarkdown from 'sites/auctions/components/CustomMarkdown'
import { getLoggedInUser } from 'redux/selectors'

import css from './sass/itempage.module.scss'
import themeCss from '../components/sass/theme.module.scss'

import ItemBid from './ItemBid'
import ItemInfoBox from './ItemInfoBox'
import { getImageUrl } from '../util/images'
import ItemEndedCard from './ItemEndedCard'
import { getBrowseItemRoute, isUserRegisteredForAuction, useResolvedAuctionUser } from './utils'
import { getAuctionScrollRestore } from '../util/auctionScrollRestore'

export default function Item(props) {  
  const [itemData, setItemData] = useState()
  const [needsRefresh, setNeedsRefresh] = useState()
  const [imageUrl, setImageUrl] = useState()
  const [auctionState, setAuctionState] = useState()
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)
  const auctionUser = useSelector(getAuctionUser)
  const currentUserId = auctionUser?.UserId
  const [initialBid, setInitialBid] = useState(null)
  
  const authenticatedUser = useSelector(getLoggedInUser)
  

  const { auctionId: queryAuctionCode, itemId: queryItemCode } = urlQueryParser(props.location.search)
  const queryClient = useQueryClient()
  const browseItemRoute = getBrowseItemRoute(queryAuctionCode, queryItemCode)
  const userAttendingAuction = isUserRegisteredForAuction(auctionUser, queryAuctionCode)
  const hasResolvedAuctionUser = useResolvedAuctionUser(authenticatedUser, auctionUser)
  const auctionScrollRestoreState = getAuctionScrollRestore(props.location)
  
  // load auction data by code, so it will work for deep-linking
  const { data: auctionDetail, queryError, isLoading } = useQuery({
    queryKey: ["auctionDetail", queryAuctionCode],
    queryFn: async () => await getAuctionByCode(queryAuctionCode),
    staleTime: 0,
    enabled: Boolean(authenticatedUser && userAttendingAuction && queryAuctionCode)
  })
  
  const auctionId = auctionDetail?.AuctionId
  
  //refresh the auctionDetail data
  if (needsRefresh) {
    let updatedItem = {...itemData, ...needsRefresh}
    queryClient.invalidateQueries(["auctionDetail"])
    setItemData(updatedItem)
    setNeedsRefresh(undefined)
  }
  
  // use effect for auction banner image
  useEffect(() => {
    const url = getImageUrl(auctionDetail?.AuctionCode, itemData?.ItemCode, itemData?.ImageFilename)
    setImageUrl(url)
  }, [auctionDetail, itemData])

  useEffect(() => {
    showLoading(isLoading)

    if (queryError || (auctionDetail && auctionDetail.queryError)) {
      console.log('Error!', queryError || auctionDetail.queryError)
    }
    else if (auctionDetail) {
      setItemData(auctionDetail.Items.find(item => item.ItemCode === queryItemCode))
      setAuctionState(getAuctionStatus(auctionDetail.StartTime, auctionDetail.EndTime))
    }
  }, [auctionDetail, isLoading, queryError, queryItemCode])

  // this is executed any time a bid is successfully updated
  // use the result to update the current bid for that item on the client
  useEffect(() => {
    if (!auctionId)
      return

    const subCreateFiltered = subscribe({query: onUpdateCurrentBidSubscription, variables: {auctionId}}, {
      next: (result) => {
        console.log('subscription result', result)
        setNeedsRefresh(result.value.data.onUpdateCurrentBid)
      },
      error: (error) => console.error("Subscription error!", error)
    })

    return () => {
      console.log('unsubscribing...')
      subCreateFiltered.unsubscribe()
    }
  }, [auctionId])

  useEffect(() => {
    console.log({auctionState})
  }, [auctionState])

  useEffect(() => {
    if (!auctionDetail || !auctionUser) return
    
    //checking registration for checked in status and text alert status
    const myRegistration = auctionUser.MyAuctions.filter(registration => {
      return registration.Auction.AuctionId === auctionDetail.AuctionId
    })[0]

    if (myRegistration) {
      setIsCheckedIn(myRegistration.IsCheckedIn)
    }

    //checking to see if auction is over and user won items
    const statusCheck = getAuctionStatus(auctionDetail.StartTime, auctionDetail.EndTime)

    if (statusCheck === 'ended') {
      //reset value
      setShowCheckout(false)

      // filter to user's items, map only the ID and if it has been paid
      const filteredItems = auctionDetail?.Items
        .filter(item => item.CurrentHighBidder === currentUserId)
        .map(item => ({ItemId: item.ItemId, Paid: item.Paid}))

      for (let i=0; i<filteredItems.length; i++) {
        //if any of the items are not paid for, show the checkout banner, and break from the loop
        if (!filteredItems[i].Paid) {
          setShowCheckout(true)
          break
        }
      }
    } 


  }, [auctionDetail, auctionUser, currentUserId])
  
  console.log('needsRefresh', needsRefresh)
  if (!authenticatedUser) return <Redirect to={browseItemRoute} />
  if (hasResolvedAuctionUser && !userAttendingAuction) return <Redirect to={browseItemRoute} />
  if (!hasResolvedAuctionUser) return (<></>)
  if (!itemData) return (<></>)

  const auctionHomeLink = {
    pathname: '/auctionhome',
    search: `?auctionId=${auctionDetail.AuctionCode}`,
    state: auctionScrollRestoreState ? { auctionScrollRestore: auctionScrollRestoreState } : undefined,
  }

  return (
    <>
      <div className={css['item-page-path']}>
        <Link className={`${css['path-text']} ${themeCss['bold-nodecoration']}`} to={auctionHomeLink}> {auctionDetail.Title} </Link>
        <p className={css['path-dash']}> / </p>
        <p className={css['path-text']}>
          {itemData.Title} 
        </p>
      </div>
      <div className={`${css['item-container']} ${themeCss['blue-background']}`}>
        { showCheckout && <ItemEndedCard auction={auctionDetail} auctionUser={auctionUser} route={'auction'} /> }

        <img className={css['item-page-img']} src={imageUrl} alt={itemData.Title} />
        
        {auctionState !== 'before' && <ItemBid item={itemData} auctionDetail={auctionDetail} auctionState={auctionState}
          isCheckedIn={isCheckedIn} outBidNotification={props.location.outbidNotification}
          initialBid={initialBid} setInitialBid={setInitialBid}
          />}
        
        <div className={css['item-bottom-info']}>
          <CustomMarkdown text={itemData.Description} cssClass={css['item-description']} />
          <ItemInfoBox item={itemData} />
        </div>
      </div>
    </>
  ) 
}
